import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { extractApiError } from '../utils/apiError';
import SocialLoginButtons from '../components/SocialLoginButtons';
import FloatingLanguageSwitcher from '../components/FloatingLanguageSwitcher';
import { EyeIcon, EyeSlashIcon } from '../components/icons/EyeIcons';
import { motion } from 'motion/react';
import { Mail, Lock, User } from 'lucide-react';
import { Card, Button, TextField, FieldError } from '../components/ui';

const TURNSTILE_SITE_KEY =
  (window as unknown as { __ENV__?: { VITE_TURNSTILE_SITE_KEY?: string } }).__ENV__
    ?.VITE_TURNSTILE_SITE_KEY || import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

/* ── Turnstile CAPTCHA (Cloudflare) ───────────────────────────────── */
function TurnstileWidget({
  onVerify,
  onExpire,
}: {
  onVerify: (token: string) => void;
  onExpire: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetId.current) return;
    widgetId.current = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: onVerify,
      'expired-callback': onExpire,
      theme: 'auto',
    });
  }, [onVerify, onExpire]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (window.turnstile) { renderWidget(); return; }
    const existing = document.querySelector('script[src*="turnstile"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      existing.addEventListener('load', renderWidget);
    }
    return () => {
      existing?.removeEventListener('load', renderWidget);
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [renderWidget]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} className="flex justify-center" />;
}

declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [consent, setConsent] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError(t('auth.register.errors.passwordMismatch')); return; }
    if (password.length < 6) { setError(t('auth.register.errors.passwordTooShort')); return; }
    if (username.length < 3) { setError(t('auth.register.errors.usernameTooShort')); return; }
    if (TURNSTILE_SITE_KEY && !captchaToken) { setError(t('auth.register.errors.captchaRequired')); return; }
    setLoading(true);
    try {
      await register(username, email, password, consent, captchaToken, acceptTerms, '2026-03');
      navigate('/verify-email', { state: { email } });
    } catch (err: unknown) {
      setError(extractApiError(err, t('auth.register.errors.default')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 dark:bg-canvas-dark">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-blue-500/10 blur-[120px] dark:bg-blue-500/15" />
        <div className="absolute -top-12 right-1/4 h-[420px] w-[420px] rounded-full bg-blue-400/8 blur-[120px] dark:bg-blue-400/10" />
        <div className="absolute bottom-0 left-1/2 h-[280px] w-[280px] -translate-x-1/2 rounded-full bg-amber-400/8 blur-[100px] dark:bg-amber-400/10" />
      </div>

      <FloatingLanguageSwitcher />

      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center gap-12 px-6 py-16 md:flex-row md:gap-20">

        {/* Left branding panel */}
        <div className="hidden flex-1 space-y-7 md:block">
          <div className="mb-6 flex items-center gap-3">
            <svg width="44" height="44" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <rect x="0.5" y="0.5" width="31" height="31" rx="8" fill="#16213A" stroke="rgba(255,255,255,.1)" />
              <path d="M9 11.5C9 9.567 10.567 8 12.5 8H22V11H13C12.4477 11 12 11.4477 12 12V20C12 20.5523 12.4477 21 13 21H22V24H12.5C10.567 24 9 22.433 9 20.5V11.5Z" fill="#fff" />
              <path d="M19 13L16 18H19L17.5 24L23 16.5H20L22 13H19Z" fill="#F59E0B" />
            </svg>
            <span className="text-[26px] font-extrabold tracking-tight text-slate-900 dark:text-white">
              Cloud<span className="text-blue-600 dark:text-blue-400">Master</span>
            </span>
          </div>

          <h2 className="text-[44px] font-extrabold leading-[1.05] tracking-[-0.03em] text-slate-900 dark:text-white lg:text-[54px]">
            {t('auth.brand.startHeadline')}<br />
            <span className="text-blue-600 dark:text-blue-400">
              {t('auth.brand.startMiddle')}
            </span><br />
            {t('auth.brand.startSubtitle')}
          </h2>

          <div className="flex gap-3 pt-4">
            {[
              { label: 'AWS', g: 'from-amber-500 to-amber-600' },
              { label: 'AZ', g: 'from-blue-500 to-blue-700' },
              { label: 'GCP', g: 'from-teal-500 to-green-600' },
            ].map(({ label, g }) => (
              <span
                key={label}
                className={`flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br ${g} text-[11px] font-extrabold text-white shadow-md`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Register card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="p-7 sm:p-8">
            <h3 className="text-[22px] font-extrabold tracking-[-0.02em] text-slate-900 dark:text-white">
              {t('auth.register.title')}
            </h3>
            <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
              {t('auth.register.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <TextField
                leftIcon={User}
                type="text"
                placeholder={t('auth.register.usernamePlaceholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
              <TextField
                leftIcon={Mail}
                type="email"
                placeholder={t('auth.register.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <TextField
                leftIcon={Lock}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.register.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                    aria-label={showPassword ? t('auth.register.hide') : t('auth.register.show')}
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                }
              />
              <TextField
                leftIcon={Lock}
                type={showConfirm ? 'text' : 'password'}
                placeholder={t('auth.register.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                    aria-label={showConfirm ? t('auth.register.hide') : t('auth.register.show')}
                  >
                    {showConfirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                }
              />

              {/* Checkboxes */}
              <div className="space-y-2.5 pt-1">
                <label className="group flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-blue-600 dark:border-white/20"
                  />
                  <span className="text-[11.5px] leading-relaxed text-slate-500 transition-colors group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-slate-200">
                    {t('auth.register.consentPrefix')}{' '}
                    <Link to="/privacy" className="font-semibold text-blue-600 hover:underline dark:text-blue-400" target="_blank">
                      {t('auth.register.consentLink')}
                    </Link>{' '}
                    {t('auth.register.consentSuffix')}
                  </span>
                </label>
                <label className="group flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-blue-600 dark:border-white/20"
                  />
                  <span className="text-[11.5px] leading-relaxed text-slate-500 transition-colors group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-slate-200">
                    {t('auth.register.termsPrefix')}{' '}
                    <Link to="/cgv" className="font-semibold text-blue-600 hover:underline dark:text-blue-400" target="_blank">
                      {t('auth.register.termsLink')}
                    </Link>{' '}
                    {t('auth.register.termsMiddle')}{' '}
                    <Link to="/privacy" className="font-semibold text-blue-600 hover:underline dark:text-blue-400" target="_blank">
                      {t('auth.register.termsPrivacyLink')}
                    </Link>
                    {t('auth.register.termsSuffix')}
                  </span>
                </label>
              </div>

              <TurnstileWidget
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken('')}
              />

              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                  <FieldError message={error} />
                </motion.div>
              )}

              <Button type="submit" variant="primary" className="mt-1 w-full" disabled={loading}>
                {loading ? t('auth.register.submitting') : t('auth.register.submit')}
              </Button>
            </form>

            <div className="relative flex items-center py-5">
              <div className="flex-grow border-t border-slate-200 dark:border-white/10" />
              <span className="mx-3 shrink-0 text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-slate-400 dark:text-slate-500">
                {t('auth.register.orContinueWith')}
              </span>
              <div className="flex-grow border-t border-slate-200 dark:border-white/10" />
            </div>

            <SocialLoginButtons mode="register" />

            <p className="mt-7 text-center text-[13px] text-slate-500 dark:text-slate-400">
              {t('auth.register.haveAccount')}{' '}
              <Link
                to="/login"
                className="font-semibold text-blue-600 underline underline-offset-4 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {t('auth.register.signInLink')}
              </Link>
            </p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

